import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    TradingAccount,
    useUpdateTradingAccount,
    useDeleteTradingAccount,
} from '@/api/trading-accounts/use-crm-trading-accounts';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatDate } from '@/lib/format';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { InlineEdit } from '@/components/ui/inline-edit';
import { useSnackbar } from 'notistack';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';
import { usePermissions } from '@/hooks/usePermissions';
import { AlertTriangle } from 'lucide-react';

interface TradingAccountDetailsModalProps {
    open: boolean;
    onClose: () => void;
    account: TradingAccount | null;
    onAccountUpdated?: () => void;
}

const TradingAccountDetailsModal: React.FC<TradingAccountDetailsModalProps> = ({
    open,
    onClose,
    account,
    onAccountUpdated,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const updateAccountMutation = useUpdateTradingAccount();
    const deleteAccountMutation = useDeleteTradingAccount();

    const handleFieldUpdate = async (field: keyof TradingAccount, value: any) => {
        if (!account) return;

        try {
            await updateAccountMutation.mutateAsync({ id: account.id, data: { [field]: value } });
            enqueueSnackbar('Trading account updated successfully', { variant: 'success' });
            onAccountUpdated?.();
        } catch (error) {
            enqueueSnackbar('Failed to update trading account', { variant: 'error' });
        }
    };

    const handleDeleteAccount = async () => {
        if (!account) return;

        try {
            await deleteAccountMutation.mutateAsync(account.id);
            enqueueSnackbar(
                'Trading account deleted successfully. All related transactions and trades have been deleted.',
                { variant: 'success' }
            );
            setShowDeleteDialog(false);
            onClose();
            onAccountUpdated?.();
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to delete trading account', {
                variant: 'error',
            });
        }
    };

    const canDelete = hasPermission(ModulePermission.TRADING_ACCOUNTS, PermissionAction.DELETE);

    if (!account) return null;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        Trading Account Details
                        {account.account_number && (
                            <Badge
                                variant="outline"
                                className="font-mono text-success border-success/30"
                            >
                                #{account.account_number}
                            </Badge>
                        )}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Account Number
                            </h3>
                            <p className="mt-1 text-sm font-mono font-medium text-success">
                                #{account.account_number || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Account ID
                            </h3>
                            <p className="mt-1 text-sm text-muted-foreground font-mono">
                                {account.id}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Base Currency
                            </h3>
                            <p className="mt-1 text-sm font-medium text-foreground">
                                {account.base_currency}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">Balance</h3>
                            <p className="mt-1 text-sm font-medium text-foreground">
                                {formatCurrency(account.balance, account.base_currency)}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">Equity</h3>
                            <p className="mt-1 text-sm font-medium text-foreground">
                                {formatCurrency(account.equity, account.base_currency)}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Used Margin
                            </h3>
                            <p className="mt-1 text-sm font-medium text-foreground">
                                {formatCurrency(account.used_margin, account.base_currency)}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Free Margin
                            </h3>
                            <p className="mt-1 text-sm font-medium text-foreground">
                                {formatCurrency(account.free_margin, account.base_currency)}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Margin Level
                            </h3>
                            <p className="mt-1 text-sm font-medium text-foreground">
                                {Number(account.margin_level || 0).toFixed(2)}%
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">Leverage</h3>
                            <InlineEdit
                                value={account.leverage.toString()}
                                onSave={value =>
                                    handleFieldUpdate('leverage', parseInt(value.toString()))
                                }
                                permissionModule={ModulePermission.TRADING_ACCOUNTS}
                                placeholder="Enter leverage"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Unrealized P&L
                            </h3>
                            <p className="mt-1 text-sm font-medium text-foreground">
                                {formatCurrency(account.unrealized_pnl, account.base_currency)}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">Daily P&L</h3>
                            <p className="mt-1 text-sm font-medium text-foreground">
                                {formatCurrency(account.daily_pnl, account.base_currency)}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Total Deposits
                            </h3>
                            <p className="mt-1 text-sm font-medium text-foreground">
                                {formatCurrency(account.total_deposits, account.base_currency)}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Total Bonuses
                            </h3>
                            <p className="mt-1 text-sm font-medium text-foreground">
                                {formatCurrency(account.total_bonuses ?? 0, account.base_currency)}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Total Withdrawals
                            </h3>
                            <p className="mt-1 text-sm font-medium text-foreground">
                                {formatCurrency(account.total_withdrawals, account.base_currency)}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">Status</h3>
                            <div className="mt-1">
                                <Badge
                                    variant="default"
                                    className={
                                        account.margin_status.can_trade
                                            ? 'bg-success/10 text-success border-success/20'
                                            : account.margin_status.margin_call
                                              ? 'bg-warning/10 text-warning border-warning/20'
                                              : 'bg-destructive/10 text-destructive border-destructive/20'
                                    }
                                >
                                    {account.margin_status.can_trade
                                        ? 'Active'
                                        : account.margin_status.margin_call
                                          ? 'Margin Call'
                                          : 'Liquidation'}
                                </Badge>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">Customer</h3>
                            <p className="mt-1 text-sm text-foreground">
                                <Link
                                    to={`/customers/${account.customer_id}`}
                                    className="text-primary hover:underline"
                                >
                                    View Customer Details
                                </Link>
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Created At
                            </h3>
                            <p className="mt-1 text-sm text-foreground">
                                {formatDate(account.created_at, 'datetime')}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Updated At
                            </h3>
                            <p className="mt-1 text-sm text-foreground">
                                {formatDate(account.updated_at, 'datetime')}
                            </p>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-sm font-medium text-muted-foreground">
                            Margin Status Details
                        </h3>
                        <div className="mt-1 space-y-1">
                            <p className="text-sm text-foreground">
                                <span className="font-medium">Can Trade:</span>{' '}
                                {account.margin_status.can_trade ? 'Yes' : 'No'}
                            </p>
                            <p className="text-sm text-foreground">
                                <span className="font-medium">Margin Call:</span>{' '}
                                {account.margin_status.margin_call ? 'Yes' : 'No'}
                            </p>
                            <p className="text-sm text-foreground">
                                <span className="font-medium">Liquidation Warning:</span>{' '}
                                {account.margin_status.liquidation_warning ? 'Yes' : 'No'}
                            </p>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    {canDelete && (
                        <Button
                            variant="destructive"
                            onClick={() => setShowDeleteDialog(true)}
                            disabled={deleteAccountMutation.isPending}
                        >
                            Delete Account
                        </Button>
                    )}
                    <Button onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertTriangle className="h-5 w-5" />
                            Delete Trading Account
                        </DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this trading account? This action cannot
                            be undone.
                            <br />
                            <br />
                            <strong>This will also delete:</strong>
                            <ul className="list-disc list-inside mt-2 space-y-1">
                                <li>All transactions related to this account</li>
                                <li>All trades related to this account</li>
                            </ul>
                            <br />
                            Account: <strong>#{account?.account_number || account?.id}</strong>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowDeleteDialog(false)}
                            disabled={deleteAccountMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteAccount}
                            disabled={deleteAccountMutation.isPending}
                        >
                            {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete Account'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
};

export default TradingAccountDetailsModal;
